import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../services/api';
import AttachmentGallery from '../Attachments/AttachmentGallery';
import styles from './Approver.module.css';

export default function ApproverPage({ user }) {
  const [queue, setQueue] = useState([]);        // open requests still to review (working queue)
  const [index, setIndex] = useState(0);          // current position in the queue
  const [appliers, setAppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [approvedIds, setApprovedIds] = useState([]); // ids approved this session
  const [done, setDone] = useState(false);        // reached the summary screen

  // per-request UI state
  const [selectedApplier, setSelectedApplier] = useState(''); // applier/category id, or 'other'
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  const [bankOptions, setBankOptions] = useState([]);
  const [gallery, setGallery] = useState(null);   // { attachments }
  // "אחר" applicant search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [pickedApplicant, setPickedApplicant] = useState(null); // { id, display }

  // summary screen
  const [summary, setSummary] = useState([]);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqs, apps] = await Promise.all([
        api.getAllOpenRequests(user.credential),
        api.getAppliers(user.credential),
      ]);
      setQueue(reqs);
      setAppliers(apps);
      // Requests already Approved (e.g. after a refresh mid-session) count as approved.
      setApprovedIds(reqs.filter(r => r.status === 'Approved').map(r => r.id));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [user.credential]);

  useEffect(() => { load(); }, [load]);

  const current = queue[index];

  // Reset per-request UI when moving between requests.
  useEffect(() => {
    setSelectedBankAccount('');
    setBankOptions([]);
    setSearchTerm('');
    setSearchResults([]);
    setPickedApplicant(null);

    // If this request was already approved, preselect its applier (category or "אחר").
    if (current && current.status === 'Approved' && current.applierId) {
      const isCategory = appliers.some(a => a.id === current.applierId);
      setSelectedApplier(isCategory ? String(current.applierId) : 'other');
      if (!isCategory) {
        setPickedApplicant({ id: current.applierId, display: 'פונה משויך' });
      }
    } else {
      setSelectedApplier('');
    }

    if (current && !current.bankAccountId) {
      api.getUserBankAccounts(user.credential, current.paymentRequesterId)
        .then(setBankOptions)
        .catch(() => setBankOptions([]));
    }
  }, [index, current, appliers, user.credential]);

  // Debounced applicant search (only when "אחר" is chosen)
  useEffect(() => {
    if (selectedApplier !== 'other') return;
    const term = searchTerm.trim();
    if (term.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      api.searchAppliers(user.credential, term)
        .then(setSearchResults)
        .catch(() => setSearchResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm, selectedApplier, user.credential]);

  const openGallery = async () => {
    try {
      const data = await api.getAttachments(user.credential, current.id);
      if (data && data.length) setGallery({ attachments: data });
      else alert('אין צרופות לבקשה זו');
    } catch (e) { alert('שגיאה בטעינת צרופות'); }
  };

  const advance = () => {
    if (index + 1 >= queue.length) setDone(true);
    else setIndex(index + 1);
  };

  const goBack = () => {
    if (done) { setDone(false); return; }
    if (index > 0) setIndex(index - 1);
  };

  const handleApprove = async () => {
    if (!selectedApplier) { alert('בחר מפעל'); return; }
    // Resolve the applier id: a category, or a searched applicant when "אחר"
    let applierId;
    if (selectedApplier === 'other') {
      if (!pickedApplicant) { alert('בחר פונה'); return; }
      applierId = pickedApplicant.id;
    } else {
      applierId = parseInt(selectedApplier);
    }
    if (!current.bankAccountId && !selectedBankAccount) { alert('שייך חשבון בנק'); return; }
    setBusy(true);
    try {
      // Assign bank account first if it was missing
      if (!current.bankAccountId && selectedBankAccount) {
        await api.assignBankAccount(user.credential, current.id, parseInt(selectedBankAccount));
      }
      await api.approveRequest(user.credential, current.id, applierId);
      // reflect new status locally + de-duplicate approved ids
      setQueue(prev => prev.map((r, i) => i === index ? { ...r, status: 'Approved', applierId } : r));
      setApprovedIds(prev => prev.includes(current.id) ? prev : [...prev, current.id]);
      advance();
    } catch (e) {
      alert('שגיאה באישור הבקשה');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    const reason = window.prompt('סיבת הדחייה:');
    if (reason == null) return;
    if (!reason.trim()) { alert('נא להזין סיבה'); return; }
    setBusy(true);
    try {
      await api.rejectRequest(user.credential, current.id, reason.trim());
      advance();
    } catch (e) {
      alert('שגיאה בדחייה');
    } finally {
      setBusy(false);
    }
  };

  const handleSkip = () => advance();

  // When reaching the summary, load the approved requests
  useEffect(() => {
    if (done && approvedIds.length > 0) {
      api.getApproved(user.credential, approvedIds).then(setSummary).catch(() => setSummary([]));
    }
  }, [done, approvedIds, user.credential]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await api.exportPdf(user.credential, approvedIds);
      const fileName = `העברות_${new Date().toLocaleDateString('he-IL').replace(/\//g, '-')}.pdf`;
      const url = URL.createObjectURL(blob);
      // Trigger a real file download (works on mobile too)
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      alert('שגיאה ביצירת PDF');
    } finally {
      setExporting(false);
    }
  };

  const formatAmount = (n) => `₪${Number(n).toLocaleString('he-IL')}`;
  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('he-IL') : '';

  if (loading) return <div className={styles.container}><p className={styles.empty}>טוען...</p></div>;

  // ---- Summary screen ----
  if (done) {
    return (
      <div className={styles.container}>
        <h2>סיכום אישורים</h2>
        {approvedIds.length === 0 ? (
          <p className={styles.empty}>לא אושרו בקשות.</p>
        ) : (
          <>
            <div className={styles.summaryWrap}>
              <table className={styles.table}>
                <thead>
                  <tr><th>מגיש</th><th>סכום</th><th>מפעל</th><th>חשבון</th></tr>
                </thead>
                <tbody>
                  {summary.map((r) => (
                    <tr key={r.id}>
                      <td>{r.requesterName}</td>
                      <td>{formatAmount(r.amount)}</td>
                      <td>{r.applierName}</td>
                      <td className={styles.bankCell}>{r.bankDisplay || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button className={styles.exportBtn} onClick={handleExport} disabled={exporting}>
              {exporting ? '⏳ מייצא...' : `הורד PDF (${approvedIds.length})`}
            </button>
          </>
        )}
        <div className={styles.summaryActions}>
          <button className={styles.secondaryBtn} onClick={goBack}>← חזור לבקשות</button>
          <button className={styles.secondaryBtn} onClick={() => { setDone(false); setIndex(0); setApprovedIds([]); load(); }}>
            רענן רשימה
          </button>
        </div>
      </div>
    );
  }

  // ---- No open requests ----
  if (queue.length === 0) {
    return (
      <div className={styles.container}>
        <h2>אישור בקשות תשלום</h2>
        <p className={styles.empty}>אין בקשות פתוחות לאישור.</p>
      </div>
    );
  }

  // ---- Per-request wizard card ----
  return (
    <div className={styles.container}>
      <div className={styles.wizardHeader}>
        <button className={styles.backBtn} onClick={goBack} disabled={index === 0}>›</button>
        <span className={styles.progress}>בקשה {index + 1} מתוך {queue.length}</span>
        <button className={styles.finishBtn} onClick={() => setDone(true)}>סיום</button>
      </div>

      <div className={styles.card}>
        <div className={styles.amount}>{formatAmount(current.amount)}</div>
        <div className={styles.requesterName}>{current.requesterName}</div>
        <div className={styles.requesterEmail}>{current.email}</div>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>תיאור הפעולה</span>
          <div className={styles.fieldValue}>{current.description || '—'}</div>
        </div>

        {current.note && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>הערה פרטית</span>
            <div className={styles.noteValue}>{current.note}</div>
          </div>
        )}

        <div className={styles.field}>
          <span className={styles.fieldLabel}>תאריך</span>
          <div className={styles.fieldValue}>{formatDate(current.syncedAt)}</div>
        </div>

        {current.hasAttachments && (
          <button className={styles.attachBtn} onClick={openGallery}>📎 הצג צרופות</button>
        )}

        {/* Bank account: shown as info if assigned, or a picker if missing */}
        {current.bankAccountId ? (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>חשבון בנק</span>
            <div className={styles.fieldValue}>{current.bankDisplay}</div>
          </div>
        ) : (
          <div className={styles.field}>
            <span className={styles.fieldLabel} style={{ color: '#e53e3e' }}>חסר חשבון בנק — בחר</span>
            <select
              className={styles.select}
              value={selectedBankAccount}
              onChange={(e) => setSelectedBankAccount(e.target.value)}
            >
              <option value="">בחר חשבון...</option>
              {bankOptions.map(a => (
                <option key={a.id} value={a.id}>{a.display}</option>
              ))}
            </select>
            {bankOptions.length === 0 && <span className={styles.hint}>אין חשבונות קודמים למגיש זה</span>}
          </div>
        )}

        {/* Applier picker = the approval */}
        <div className={styles.field}>
          <span className={styles.fieldLabel}>שייך למפעל</span>
          <select
            className={styles.select}
            value={selectedApplier}
            onChange={(e) => { setSelectedApplier(e.target.value); setPickedApplicant(null); setSearchTerm(''); setSearchResults([]); }}
          >
            <option value="">בחר מפעל...</option>
            {appliers.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
            <option value="other">אחר (חיפוש פונה)</option>
          </select>
        </div>

        {/* "אחר" -> smart applicant search */}
        {selectedApplier === 'other' && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>חפש פונה</span>
            {pickedApplicant ? (
              <div className={styles.pickedApplicant}>
                <span>{pickedApplicant.display}</span>
                <button className={styles.clearPick} onClick={() => { setPickedApplicant(null); setSearchTerm(''); }}>שנה</button>
              </div>
            ) : (
              <>
                <input
                  className={styles.select}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="שם / בן זוג / משפחה / רחוב / עיר..."
                  autoFocus
                />
                {searchResults.length > 0 && (
                  <div className={styles.searchResults}>
                    {searchResults.map(a => (
                      <button key={a.id} className={styles.searchResult} onClick={() => setPickedApplicant(a)}>
                        {a.display}
                      </button>
                    ))}
                  </div>
                )}
                {searchTerm.trim().length >= 2 && searchResults.length === 0 && (
                  <span className={styles.hint}>לא נמצאו תוצאות</span>
                )}
              </>
            )}
          </div>
        )}

        <div className={styles.actionRow}>
          <button className={styles.approveBtn} onClick={handleApprove} disabled={busy}>אשר</button>
          <button className={styles.skipBtn} onClick={handleSkip} disabled={busy}>דלג</button>
          <button className={styles.rejectBtn} onClick={handleReject} disabled={busy}>דחה</button>
        </div>
      </div>

      {gallery && (
        <AttachmentGallery
          attachments={gallery.attachments}
          user={user}
          canDelete={false}
          onClose={() => setGallery(null)}
        />
      )}
    </div>
  );
}
